import { Injectable, inject } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as Sentry from '@sentry/angular';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { Subject, Subscription, lastValueFrom, of, retry, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private auth = inject(AuthFacade);
  private config = inject(ConfigurationService);
  private environment = inject(EnvironmentService);

  public onclose = new Subject();

  public authorizedZoneConnection: HubConnection;
  public nonAuthorizedZoneConnection: HubConnection;

  private authorizedReconnectSubscription: Subscription;
  private nonAuthorizedReconnectSubscription: Subscription;

  constructor() {
    this.createAuthorizedConnection();
  }

  public stopReconnection() {
    this.nonAuthorizedReconnectSubscription?.unsubscribe();
    this.authorizedReconnectSubscription?.unsubscribe();
  }

  private createConnection(url: string, token?: string) {
    return new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => {
          if (token) {
            return token;
          }
          return this.auth.accessToken;
        },
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        logger: LogLevel.Debug
      })
      .withAutomaticReconnect()
      .build();
  }

  private createAuthorizedConnection() {
    this.authorizedZoneConnection = this.createConnection(this.config.get('signalRAuthorizedZoneHub'));
  }

  private createNonAuthorizedConnection(authId: string) {
    this.nonAuthorizedZoneConnection = this.createConnection(this.config.get('signalRNonAuthorizedZoneHub'), authId);
  }

  private startConnection(connection: HubConnection, errorCallback?: Function) {
    connection?.stop();

    const subscription = of(null)
      .pipe(
        switchMap(() => connection.start()),
        retry({
          count: 20,
          delay: (error, retryCount) => {
            if (retryCount === 1 && error) errorCallback?.();
            const delayMs = 1000 * Math.pow(2, retryCount);
            return timer(delayMs);
          }
        })
      )
      .subscribe({
        error: (error) => {
          this.sendLog('Failed to connect to web sockets', { error });
          this.onclose.next(true);
        }
      });

    connection.onclose((error) => {
      this.sendLog('Close connection to web sockets', { error });

      this.onclose.next(error);
    });

    this.startLoggingTopics(connection);

    return {
      connection,
      subscription
    };
  }

  public restartAuthorizedConnection() {
    this.auth.tryRefreshToken$().subscribe(() => {
      this.startAuthorizedConnection();
    });
  }

  public startAuthorizedConnection() {
    const callback = () => lastValueFrom(this.auth.tryRefreshToken$());
    this.authorizedReconnectSubscription = this.startConnection(this.authorizedZoneConnection, callback).subscription;
  }

  public startNonAuthorizedConnection(authId: string, callback?: Function): HubConnection {
    this.nonAuthorizedZoneConnection?.stop();
    this.createNonAuthorizedConnection(authId);

    const { connection, subscription } = this.startConnection(this.nonAuthorizedZoneConnection, callback);
    this.nonAuthorizedReconnectSubscription = subscription;

    return connection;
  }

  public stopConnections() {
    this.nonAuthorizedZoneConnection?.stop();
    this.authorizedZoneConnection?.stop();
  }

  public stopNonAuthorizedConnection() {
    this.nonAuthorizedZoneConnection?.stop().then();
  }

  private startLoggingTopics(connection: HubConnection) {
    this.config.get('loggedWSTopics')?.forEach((topic: string) => {
      connection.on(topic, (result) => {
        this.sendLog(topic, result);
      });
    });
  }

  private sendLog(message: string, extras?: any) {
    if (!this.environment.production) {
      console.log({
        message,
        extras
      });
      return;
    }
    Sentry.captureMessage(message, {
      level: 'info',
      tags: {
        type: 'signalR'
      },
      extra: extras
    });
  }
}
